"use client";

import React, { useMemo, useRef, useState, useEffect } from "react";
import Image from "next/image";
import { Plus } from "lucide-react";
import { Icon } from "@repo/ui/icon";
import { Heading } from "@repo/ui/heading";
import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

const positionBySlug: Record<string, { top: string; left: string }> = {
  littau: { top: "50%", left: "45%" },
  luzern: { top: "38%", left: "42%" },
  dottingen: { top: "30%", left: "50%" },
  basel: { top: "30%", left: "35%" },
  lms: { top: "55%", left: "46%" },
};

const COMING_SOON: Array<{
  id: string;
  name: string;
  position: { top: string; left: string };
}> = [
  // { id: "basel", name: "Basel", position: { top: "30%", left: "35%" } },
];

export default function SwissMapTooltip({
  headline,
  hotels,
}: {
  headline?: any;
  hotels: any[];
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);

  const enriched = useMemo(() => {
    return (hotels || []).map((h: any) => {
      const slug = h.slug;
      const pos = positionBySlug[slug] || { top: "50%", left: "50%" };
      const fmt = h.image?.formats;
      const img = h.image?.url || fmt?.medium?.url || fmt?.small?.url || "";
      // Check if it has an external URL for linking
      const hasExternalUrl = h.url && h.url.trim() !== "" && (h.url.startsWith('http://') || h.url.startsWith('https://'));
      
      return {
        id: slug,
        name: h.title ?? slug,
        descriptionBlocks: Array.isArray(h.description) ? h.description : null,
        descriptionText: Array.isArray(h.description)
          ? ""
          : (h.description ?? ""),
        position: pos,
        items: Array.isArray(h.items) ? h.items : [],
        href: hasExternalUrl ? h.url : "#",
        preview: absoluteCmsUrl(img),
        comingSoon: !hasExternalUrl, // 🔹 mark as coming soon if no external URL
      };
    });
  }, [hotels]);

  /* 🔹 Merge Strapi pins with static “coming soon” pins */
  const pins = useMemo(() => {
    const soon = COMING_SOON.map((p) => ({
      ...p,
      descriptionBlocks: null,
      descriptionText: "Coming soon",
      items: [],
      href: "",
      preview: "",
      comingSoon: true as const,
    }));
    return [...enriched, ...soon];
  }, [enriched]);

  const [selectedHotel, setSelectedHotel] = useState<
    null | (typeof pins)[number]
  >(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const handleOpen = (e: React.MouseEvent, hotel: (typeof pins)[number]) => {
    if (selectedHotel?.id === hotel.id) {
      setSelectedHotel(null);
      return;
    }
    const button = e.currentTarget as HTMLButtonElement;
    const rect = button.getBoundingClientRect();
    const containerRect = mapRef.current?.getBoundingClientRect();
    const offsetTop = rect.top - (containerRect?.top || 0);
    const offsetLeft = rect.left - (containerRect?.left || 0);
    setTooltipPos({ top: offsetTop, left: offsetLeft });
    setSelectedHotel(hotel);
  };

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setSelectedHotel(null);
    if (selectedHotel) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedHotel]);

  return (
    <div
      ref={mapRef}
      className="relative w-full px-4 sm:block hidden reveal-stagger py-24"
    >
      {headline?.heading?.title && (
        <div className="max-w-7xl mx-auto flex flex-col gap-6 pt-12">
          <Heading
            level={headline.heading.level}
            className="xl:text-6xl md:text-5xl text-4xl tracking-tighter text-center reveal-item"
          >
            {headline.heading.title}
          </Heading>
          {headline.description && (
            <div className="text-grey-900 md:text-lg text-base max-w-3xl mx-auto text-center reveal-item">
              <BlocksRenderer content={headline.description} />
            </div>
          )}
        </div>
      )}

      <Image
        src="/assets/swiss-map2.svg"
        alt="Switzerland Map"
        width={1600}
        height={1200}
        loading="lazy"
        className="max-w-screen-2xl w-full mx-auto h-auto reveal-item"
      />

      {/* 🔹 Render pins for both live and coming-soon */}
      {pins.map((hotel) => (
        <div
          key={hotel.id}
          className="absolute z-10 flex flex-col gap-1"
          style={{ ...hotel.position }}
        >
          <span className="text-grey-900">{hotel.name}</span>
          <button
            onClick={(e) => handleOpen(e, hotel)}
            className="w-fit mx-auto p-1 bg-black rounded-full hover:scale-110 transition cursor-pointer"
            aria-label={`More info about ${hotel.name}${hotel.comingSoon ? " (coming soon)" : ""}`}
          >
            <Plus className="w-6 h-6 text-white" />
          </button>
        </div>
      ))}

      {selectedHotel && (
        <div
          className="absolute z-20 reveal-item"
          onClick={(e) => e.stopPropagation()}
          style={{ top: tooltipPos.top, left: tooltipPos.left + 40 }}
        >
          <div className="bg-white shadow-xl rounded-2xl relative flex flex-row overflow-hidden">
            {/* Show image for all hotels */}
            {selectedHotel.preview && (
              <div>
                <BaseImage
                  className="h-full flex-shrink-0 object-cover"
                  width={250}
                  height={300}
                  src={selectedHotel.preview || ""}
                  alt={`${selectedHotel.name} preview`}
                />
              </div>
            )}

            <div className="max-w-md flex flex-col p-8 gap-8">
              <div className="flex flex-col gap-3">
                {/* Show title with or without link based on external URL */}
                {selectedHotel.comingSoon ? (
                  <div className="text-2xl font-semibold border-b border-gray-200 pb-3 flex items-center gap-2 flex-row justify-between">
                    {selectedHotel.name}
                    <span className="text-xs bg-black text-white rounded-full px-2 py-0.5">
                      Coming soon
                    </span>
                  </div>
                ) : (
                  <a
                    href={selectedHotel.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-2xl font-semibold border-b border-gray-200 pb-3 flex items-center gap-1 hover:underline"
                  >
                    {selectedHotel.name}
                    <Icon name="arrow-up-right" size={"16"} />
                  </a>
                )}

                {/* Show description for all hotels */}
                {selectedHotel.descriptionBlocks ? (
                  <div className="text-grey-900">
                    <BlocksRenderer content={selectedHotel.descriptionBlocks} />
                  </div>
                ) : (
                  <p className="text-grey-900">
                    {selectedHotel.descriptionText}
                  </p>
                )}
              </div>

              {/* Show items for all hotels */}
              {selectedHotel.items && selectedHotel.items.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {selectedHotel.items.map((item: any) => (
                    <li
                      key={item.id}
                      className="bg-grey-400 text-sm text-grey-900 py-1 px-3 font-medium rounded-3xl tracking-tighter"
                    >
                      {item.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
